import React from 'react';

import { gql, useLazyQuery } from '@apollo/client';
import type { CardProps } from '@mui/material';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { find, isEmpty, map, sortBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { calJobsPositionCountMap, formatDate } from '../../../utils/libs';
import GroupJobsCard from './group_jobs_card';
import { JobCardItem } from './job_card_item';

import type { PeriodJob } from '../../../graphql/graphql';

const getPeriodJobsGql = gql(`
  query periodJobs($periodJobId: ID) {
    periodJobs(id: $periodJobId) {
      id
      periodStartAt
      periodEndAt
      requiredDayCount
      jobs {
        id
        jobType
        status
        numberOfPosition
        scheduleDate
        jobStartAt
        jobEndAt
        expiredAt
        isImmediateAccept
        jobApplicants {
          id
          status
        }
        jobHirings {
          id
          status
        }
        periodJob {
          id
        }
      }
    }
  }
`);

export type PeriodJobCardProps = {
  periodJob: PeriodJob;
} & CardProps;

const PeriodJobCard = React.memo(
  React.forwardRef<HTMLDivElement, PeriodJobCardProps>(({ periodJob, ...CardProps }, ref) => {
    const periodJobId = periodJob.id;
    const navigate = useNavigate();

    const [fetchData, { data, loading }] = useLazyQuery(getPeriodJobsGql, {
      fetchPolicy: 'cache-and-network',
      variables: { periodJobId },
      notifyOnNetworkStatusChange: true,
    });

    const periodJobInternal = React.useMemo(
      () => find(data?.periodJobs, (_periodJob) => _periodJob?.id === periodJobId) ?? periodJob,
      [data?.periodJobs, periodJobId],
    );

    const refetchHandler = React.useCallback(async () => {
      if (periodJobId) {
        await fetchData({ variables: { periodJobId } });
      }
    }, [fetchData, periodJobId]);

    const onCardHeaderClickHandler = React.useCallback(() => {
      navigate({ pathname: `period_jobs/${periodJobId}` });
    }, [periodJobId]);

    const jobs = React.useMemo(
      () => sortBy(periodJobInternal?.jobs, (job) => dayjs(job.jobStartAt)),
      [periodJobInternal?.jobs],
    );
    const jobsPositionCountMap = React.useMemo(() => calJobsPositionCountMap(jobs), [jobs]);

    return (
      <GroupJobsCard
        ref={ref}
        title={
          <>
            {formatDate(periodJobInternal.periodStartAt, 'ddd DD MMM')}
            {' - '}
            {formatDate(periodJobInternal.periodEndAt, 'ddd DD MMM')}
          </>
        }
        loading={loading}
        isEmpty={isEmpty(jobs)}
        onHeaderClick={onCardHeaderClickHandler}
        onRefresh={refetchHandler}
        {...CardProps}
      >
        <Stack gap={1}>
          {map(jobs, (job) => (
            <JobCardItem
              key={job.id}
              showScheduleDate
              disableEndedJob
              isPeriodJob
              job={job}
              navigateTo={`/jobs/${job.id}`}
              applicantCount={jobsPositionCountMap[job.id].applicantCount}
              hiringCount={jobsPositionCountMap[job.id].hiringCount}
            />
          ))}
        </Stack>
      </GroupJobsCard>
    );
  }),
);

export default PeriodJobCard;
